import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import Header from '../components/Header';
import { AlertCard, ChartCard, PageHeader, StatisticCard, StatusBadge } from '../components/ui/PremiumUI';
import api from '../services/api';

const currency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const number = (value, suffix = '') =>
  `${Number(value || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}${suffix}`;

const weatherLabels = {
  0: 'Ceu limpo',
  1: 'Principalmente limpo',
  2: 'Parcialmente nublado',
  3: 'Nublado',
  45: 'Neblina',
  48: 'Neblina com geada',
  51: 'Garoa fraca',
  53: 'Garoa moderada',
  55: 'Garoa intensa',
  61: 'Chuva fraca',
  63: 'Chuva moderada',
  65: 'Chuva forte',
  80: 'Pancadas fracas',
  81: 'Pancadas moderadas',
  82: 'Pancadas fortes',
  95: 'Temporal'
};

const chartColors = ['var(--cm-kpi-green)', 'var(--cm-kpi-blue)', 'var(--cm-kpi-orange)', 'var(--cm-kpi-red)', '#a78bfa'];

function Dashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [pecuaria, setPecuaria] = useState(null);
  const [estoques, setEstoques] = useState([]);
  const [safras, setSafras] = useState([]);
  const [propriedades, setPropriedades] = useState([]);
  const [fazendaSelecionadaId, setFazendaSelecionadaId] = useState('');
  const [weather, setWeather] = useState(null);
  const [weatherError, setWeatherError] = useState('');
  const [weatherLocation, setWeatherLocation] = useState(null);
  const [weatherRefreshKey, setWeatherRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [aplicacoesPendentes, setAplicacoesPendentes] = useState([]);
  const [indicadoresGestao, setIndicadoresGestao] = useState(null);
  const weatherRequestRef = useRef(0);

  const fazendaSelecionada = useMemo(() => {
    if (!propriedades.length) return null;
    return propriedades.find((item) => String(item.id) === String(fazendaSelecionadaId)) || propriedades[0];
  }, [fazendaSelecionadaId, propriedades]);

  const safrasFiltradas = useMemo(() => {
    if (!fazendaSelecionada?.id) return safras;
    return safras.filter((safra) => String(safra.propriedadeId) === String(fazendaSelecionada.id));
  }, [fazendaSelecionada, safras]);

  const estoqueBaixo = dashboard?.estoque?.produtosEstoqueBaixo ?? 0;

  const financeiroChart = useMemo(() => {
    const receitas = Number(dashboard?.financeiro?.receitasMes || 0);
    const despesas = Number(dashboard?.financeiro?.despesasMes || 0);
    const saldo = Number(dashboard?.financeiro?.saldoAtual || 0);
    return [
      { name: 'Receitas', valor: receitas },
      { name: 'Despesas', valor: despesas },
      { name: 'Saldo', valor: saldo }
    ];
  }, [dashboard]);

  const producaoChart = useMemo(() => [
    { name: 'Área ha', valor: Number(dashboard?.producao?.areaPlantadaHa || 0) },
    { name: 'Colhido kg', valor: Number(dashboard?.producao?.producaoUltimos30DiasKg || 0) },
    { name: 'Insumos', valor: Number(dashboard?.estoque?.estoqueInsumos || 0) },
    { name: 'Estoque agrícola', valor: Number(dashboard?.estoque?.estoqueAgricola || 0) }
  ], [dashboard]);

  const pecuariaChart = useMemo(() => [
    { name: 'Ativos', valor: Number(pecuaria?.animaisAtivos || 0) },
    { name: 'Nascimentos', valor: Number(pecuaria?.nascimentosMes || 0) },
    { name: 'Mortes', valor: Number(pecuaria?.mortesMes || 0) },
    { name: 'Vacinas', valor: Number(pecuaria?.vacinasPendentes || 0) }
  ], [pecuaria]);

  const cards = [
    { title: 'Safras em andamento', value: dashboard?.producao?.safrasAtivas ?? 0, icon: 'bi-calendar4-week', tone: 'green', helper: 'Ciclos ativos', trend: 'Agro' },
    { title: 'Receita do mês', value: currency(dashboard?.financeiro?.receitasMes), icon: 'bi-arrow-down-circle', tone: 'green', helper: 'Entradas confirmadas', trend: '+ caixa' },
    { title: 'Despesas do mês', value: currency(dashboard?.financeiro?.despesasMes), icon: 'bi-arrow-up-circle', tone: 'orange', helper: 'Saídas do período', trend: 'custos' },
    { title: 'Saldo financeiro', value: currency(dashboard?.financeiro?.saldoAtual), icon: 'bi-cash-coin', tone: 'blue', helper: 'Posição atual', trend: 'liquidez' },
    { title: 'Estoque baixo', value: estoqueBaixo, icon: 'bi-exclamation-triangle', tone: estoqueBaixo > 0 ? 'red' : 'green', helper: 'Itens em alerta', trend: estoqueBaixo > 0 ? 'ação' : 'ok' },
    { title: 'Produção nos últimos 30 dias', value: number(dashboard?.producao?.producaoUltimos30DiasKg, ' kg'), icon: 'bi-basket-fill', tone: 'teal', helper: 'Volume recente', trend: 'campo' },
    { title: 'Cabeças ativas', value: pecuaria?.animaisAtivos ?? 0, icon: 'bi-collection', tone: 'blue', helper: 'Rebanho em controle', trend: 'pecuaria' },
    { title: 'Lucro pecuário', value: currency(pecuaria?.lucroPecuaria), icon: 'bi-graph-up-arrow', tone: 'green', helper: 'Resultado estimado', trend: 'margem' },
    ...(indicadoresGestao ? [
      { title: 'Custo por hectare', value: currency(indicadoresGestao.custoHa), icon: 'bi-calculator', tone: 'orange', helper: indicadoresGestao.safra, trend: 'motor gerencial' },
      { title: 'Custo por saca', value: currency(indicadoresGestao.custoSaca), icon: 'bi-bag-check', tone: 'teal', helper: indicadoresGestao.safra, trend: '60 kg' },
      { title: 'Resultado da safra', value: currency(indicadoresGestao.resultado), icon: 'bi-graph-up', tone: Number(indicadoresGestao.resultado || 0) >= 0 ? 'green' : 'red', helper: indicadoresGestao.safra, trend: 'integrado' },
      { title: 'Margem da safra', value: number(indicadoresGestao.margem, '%'), icon: 'bi-percent', tone: Number(indicadoresGestao.margem || 0) >= 0 ? 'green' : 'red', helper: indicadoresGestao.safra, trend: 'integrado' }
    ] : [])
  ];

  const carregarIndicadoresGestao = async (safrasDisponiveis, propriedadeId) => {
    const candidatas = (Array.isArray(safrasDisponiveis) ? safrasDisponiveis : [])
      .filter((safra) => !propriedadeId || String(safra.propriedadeId) === String(propriedadeId));
    const safra = candidatas[0];
    if (!safra?.id) { setIndicadoresGestao(null); return; }
    try {
      const response = await api.post('/ferramentas-gestao/calcular', {
        ferramenta: 'resultado-safra', modo: 'REAL', filtros: { safraId: Number(safra.id) }, valores: {}
      });
      const valor = (codigo) => response.data?.indicadores?.find((item) => item.codigo === codigo)?.valor ?? null;
      setIndicadoresGestao({
        safra: `${safra.cultura || 'Safra'}${safra.talhaoNome || safra.talhao?.nome ? ` · ${safra.talhaoNome || safra.talhao?.nome}` : ''}`,
        custoHa: valor('custoHa'), custoSaca: valor('custoSaca'), resultado: valor('resultado'), margem: valor('margem')
      });
    } catch (error) {
      // Perfis sem acesso aos indicadores gerenciais continuam usando o painel operacional normalmente.
      setIndicadoresGestao(null);
      if (error.response?.status !== 403) console.error('Erro ao carregar indicadores gerenciais', error);
    }
  };

  const carregarClima = useCallback(async (fazenda) => {
    const requestId = ++weatherRequestRef.current;
    setWeather(null);
    setWeatherError('');
    setWeatherLocation(null);
    if (!fazenda) { setWeatherLoading(false); return; }
    setWeatherLoading(true);
    try {
      let latitude = fazenda.latitude;
      let longitude = fazenda.longitude;

      // Cidade/UF representam a escolha atual do usuário. Coordenadas antigas podem
      // permanecer em cadastros legados e não devem manter a previsão de outra cidade.
      if (fazenda.cidade) {
        const locationRes = await api.get('/geocodificacao', { params: { cidade: fazenda.cidade, estado: fazenda.estado || '' } });
        latitude = locationRes.data.latitude;
        longitude = locationRes.data.longitude;
      }

      if (latitude == null || longitude == null) {
        throw new Error('Localização não cadastrada');
      }

      const params = new URLSearchParams({
        latitude: String(latitude),
        longitude: String(longitude),
        current: 'temperature_2m,relative_humidity_2m,precipitation,weather_code,wind_speed_10m',
        daily: 'weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max',
        timezone: 'auto',
        forecast_days: '5'
      });
      const res = await fetch(`https://api.open-meteo.com/v1/forecast?${params.toString()}`);
      if (!res.ok) throw new Error('Falha ao buscar clima');
      const data = await res.json();
      if (requestId !== weatherRequestRef.current) return;
      setWeather(data);
      setWeatherLocation({ latitude, longitude, cidade: fazenda.cidade, estado: fazenda.estado });
    } catch (error) {
      console.error('Erro ao carregar previsão do tempo', error);
      if (requestId !== weatherRequestRef.current) return;
      setWeather(null);
      setWeatherError(error.message === 'Localização não cadastrada'
        ? 'Informe a cidade ou marque a localização desta fazenda para consultar o clima.'
        : 'Não foi possível atualizar a previsão agora. Tente novamente.');
    } finally {
      if (requestId === weatherRequestRef.current) setWeatherLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarClima(fazendaSelecionada);
  }, [carregarClima, fazendaSelecionada?.id, fazendaSelecionada?.latitude, fazendaSelecionada?.longitude, fazendaSelecionada?.cidade, fazendaSelecionada?.estado, weatherRefreshKey]);

  const carregar = async (propriedadeId = fazendaSelecionadaId) => {
    setLoading(true);
    try {
      const propriedadesRes = await api.get('/propriedades');
      const propriedadesData = Array.isArray(propriedadesRes.data) ? propriedadesRes.data : [];
      const fazendaInicial =
        propriedadesData.find((item) => String(item.id) === String(propriedadeId)) ||
        propriedadesData.find((item) => item.ativo && item.latitude != null && item.longitude != null) ||
        propriedadesData.find((item) => item.ativo) ||
        propriedadesData[0];
      const propriedadeSelecionadaId = fazendaInicial?.id ? String(fazendaInicial.id) : '';

      const [dashRes, pecuariaRes, estoquesRes, safrasRes] = await Promise.all([
        api.get('/dashboard/consolidado', {
          params: propriedadeSelecionadaId ? { propriedadeId: propriedadeSelecionadaId } : {}
        }),
        api.get('/pecuaria/dashboard'),
        api.get('/producao-agricola/estoques'),
        api.get('/safras')
      ]);

      setDashboard(dashRes.data);
      setPecuaria(pecuariaRes.data);
      setEstoques(Array.isArray(estoquesRes.data) ? estoquesRes.data : []);
      const safrasData = Array.isArray(safrasRes.data) ? safrasRes.data : [];
      setSafras(safrasData);
      setPropriedades(propriedadesData);
      setFazendaSelecionadaId(propriedadeSelecionadaId);
      await carregarIndicadoresGestao(safrasData, propriedadeSelecionadaId);

      setWeatherRefreshKey((current) => current + 1);
    } catch (error) {
      console.error('Erro ao carregar dashboard agro', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  useEffect(() => {
    const carregarAplicacoes = () => {
      let fluxos = [];
      try { fluxos = JSON.parse(localStorage.getItem('cultivamais.fluxo-plantio.avaliacao') || '[]'); } catch { fluxos = []; }
      const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
      setAplicacoesPendentes((Array.isArray(fluxos) ? fluxos : []).filter(x => x.dataPrevista).map(x => {
        const prevista = new Date(`${x.dataPrevista}T00:00:00`);
        const dias = Math.ceil((prevista - hoje) / 86400000);
        return { ...x, dias, prevista };
      }).filter(x => x.dias <= 5).sort((a, b) => a.dias - b.dias));
    };
    carregarAplicacoes();
    window.addEventListener('storage', carregarAplicacoes);
    window.addEventListener('cultivamais:lembretes-atualizados', carregarAplicacoes);
    return () => { window.removeEventListener('storage', carregarAplicacoes); window.removeEventListener('cultivamais:lembretes-atualizados', carregarAplicacoes); };
  }, []);

  const selecionarFazenda = async (event) => {
    const id = event.target.value;
    setFazendaSelecionadaId(id);
    try {
      setLoading(true);
      const dashRes = await api.get('/dashboard/consolidado', {
        params: id ? { propriedadeId: id } : {}
      });
      setDashboard(dashRes.data);
      await carregarIndicadoresGestao(safras, id);
    } catch (error) {
      console.error('Erro ao carregar indicadores da fazenda', error);
    } finally {
      setLoading(false);
    }
  };

  const dailyWeather = weather?.daily?.time?.map((dia, index) => ({
    dia: new Date(`${dia}T00:00:00`).toLocaleDateString('pt-BR', { weekday: 'short' }),
    max: weather.daily.temperature_2m_max?.[index] ?? 0,
    min: weather.daily.temperature_2m_min?.[index] ?? 0,
    chuva: weather.daily.precipitation_probability_max?.[index] ?? 0,
    codigo: weather.daily.weather_code?.[index] ?? 0
  })) ?? [];

  const alertas = [
    estoqueBaixo > 0 && {
      priority: 'danger',
      title: 'Estoque crítico',
      description: `${estoqueBaixo} ${estoqueBaixo === 1 ? 'item precisa' : 'itens precisam'} de reposição.`,
      icon: <i className="bi bi-exclamation-triangle" />
    },
    pecuaria?.vacinasPendentes > 0 && {
      priority: 'warning',
      title: 'Vacinas pendentes',
      description: `${pecuaria.vacinasPendentes} ${pecuaria.vacinasPendentes === 1 ? 'evento sanitário aguarda' : 'eventos sanitários aguardam'} ação.`,
      icon: <i className="bi bi-shield-exclamation" />
    },
    !safrasFiltradas.length && {
      priority: 'info',
      title: 'Safra inicial',
      description: 'Cadastre uma safra para liberar indicadores de produção e fechamento.',
      icon: <i className="bi bi-calendar-plus" />
    }
  ].filter(Boolean);

  return (
    <>
      <Header />
      <div className="cm-page">
        <PageHeader
          eyebrow="Centro de comando"
          title="Como esta sua fazenda hoje?"
          description="Indicadores de safra, estoque, financeiro, pecuária e clima para tomada de decisão."
          actions={(
            <>
              <div style={{ minWidth: 260 }}>
                <label className="form-label small text-muted mb-1">Fazenda</label>
                <select
                  className="form-select"
                  value={fazendaSelecionadaId}
                  onChange={selecionarFazenda}
                  disabled={!propriedades.length}
                >
                  {!propriedades.length && <option value="">Nenhuma fazenda cadastrada</option>}
                  {propriedades.map((fazenda) => (
                    <option value={fazenda.id} key={fazenda.id}>
                      {fazenda.nome}
                    </option>
                  ))}
                </select>
              </div>
              <Link className="btn btn-outline-success fw-semibold" to="/fluxo-erp">
                <i className="bi bi-diagram-3 me-1" />
                Consultar fluxo
              </Link>
              <button className="btn btn-success fw-semibold" onClick={() => carregar()} disabled={loading}>
                <i className="bi bi-arrow-clockwise me-1" />
                Atualizar
              </button>
            </>
          )}
        />

          <div className="row g-3 mb-4">
            {cards.map((card) => (
              <div className="col-12 col-sm-6 col-xl-3" key={card.title}>
                <StatisticCard
                  title={card.title}
                  value={card.value}
                  tone={card.tone}
                  helper={card.helper}
                  trend={card.trend}
                  icon={<i className={`bi ${card.icon}`} />}
                />
              </div>
            ))}
          </div>

          <div className="row g-4 mb-4">
            <div className="col-12 col-xl-5">
              <ChartCard title="Alertas inteligentes" subtitle="Prioridades que merecem sua atenção hoje">
                <div className="d-grid gap-2">
                  {alertas.map((alerta) => (
                    <AlertCard key={alerta.title} {...alerta} />
                  ))}
                  {aplicacoesPendentes.map((item) => (
                    <Link to="/fluxo-plantio" className="text-decoration-none" key={item.id}>
                      <AlertCard priority={item.dias < 0 ? 'danger' : item.dias <= 2 ? 'warning' : 'info'} title={`${item.talhao} · próxima aplicação`} description={item.dias < 0 ? `Atrasada há ${Math.abs(item.dias)} ${Math.abs(item.dias) === 1 ? 'dia' : 'dias'}.` : item.dias === 0 ? 'Aplicação prevista para hoje.' : `Aplicação em ${item.dias} ${item.dias === 1 ? 'dia' : 'dias'} — ${item.prevista.toLocaleDateString('pt-BR')}.`} icon={<i className="bi bi-calendar-event" />} />
                    </Link>
                  ))}
                  {!alertas.length && !aplicacoesPendentes.length && (
                    <AlertCard
                      priority="info"
                      title="Operação estável"
                      description="Nenhum alerta crítico encontrado para a fazenda selecionada."
                      icon={<i className="bi bi-check2-circle" />}
                    />
                  )}
                </div>
              </ChartCard>
            </div>

            <div className="col-12 col-xl-7">
              <ChartCard
                title="Resumo executivo"
                subtitle="Leitura rápida da operação selecionada"
                actions={fazendaSelecionada && <StatusBadge tone="success">{fazendaSelecionada.nome}</StatusBadge>}
              >
                <div className="row g-3">
                  <div className="col-6 col-lg-3">
                    <div className="cm-surface p-3 h-100">
                      <span className="cm-muted small d-block">Safras</span>
                      <strong>{safrasFiltradas.length}</strong>
                    </div>
                  </div>
                  <div className="col-6 col-lg-3">
                    <div className="cm-surface p-3 h-100">
                      <span className="cm-muted small d-block">Estoques</span>
                      <strong>{estoques.length}</strong>
                    </div>
                  </div>
                  <div className="col-6 col-lg-3">
                    <div className="cm-surface p-3 h-100">
                      <span className="cm-muted small d-block">Área plantada</span>
                      <strong>{number(dashboard?.producao?.areaPlantadaHa, ' ha')}</strong>
                    </div>
                  </div>
                  <div className="col-6 col-lg-3">
                    <div className="cm-surface p-3 h-100">
                      <span className="cm-muted small d-block">Saldo</span>
                      <strong className="cm-money">{currency(dashboard?.financeiro?.saldoAtual)}</strong>
                    </div>
                  </div>
                </div>
              </ChartCard>
            </div>
          </div>

          <div className="row g-4 mb-4">
            <div className="col-12 col-xl-4">
              <ChartCard title="Financeiro do mês" subtitle="Receitas, despesas e saldo">
                  <ResponsiveContainer width="100%" height={230}>
                    <BarChart data={financeiroChart}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={(value) => Number(value).toLocaleString('pt-BR')} />
                      <Tooltip formatter={(value) => currency(value)} />
                      <Bar dataKey="valor" radius={[6, 6, 0, 0]}>
                        {financeiroChart.map((entry, index) => (
                          <Cell key={entry.name} fill={chartColors[index]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
              </ChartCard>
            </div>

            <div className="col-12 col-xl-4">
              <ChartCard title="Produção e estoque" subtitle="Volume, área e disponibilidade">
                  <ResponsiveContainer width="100%" height={230}>
                    <AreaChart data={producaoChart}>
                      <defs>
                        <linearGradient id="producaoGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--cm-primary)" stopOpacity={0.75} />
                          <stop offset="95%" stopColor="var(--cm-primary)" stopOpacity={0.08} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => number(value)} />
                      <Area type="monotone" dataKey="valor" stroke="var(--cm-primary)" fill="url(#producaoGradient)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
              </ChartCard>
            </div>

            <div className="col-12 col-xl-4">
              <ChartCard title="Pecuária" subtitle="Rebanho, eventos e sanidade">
                  <ResponsiveContainer width="100%" height={230}>
                    <PieChart>
                      <Pie data={pecuariaChart} dataKey="valor" nameKey="name" innerRadius={58} outerRadius={86} paddingAngle={3}>
                        {pecuariaChart.map((entry, index) => (
                          <Cell key={entry.name} fill={chartColors[index]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => number(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
              </ChartCard>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-12 col-xl-7">
              <ChartCard
                title="Clima na fazenda"
                subtitle="Previsão e condições atuais"
                actions={fazendaSelecionada && <StatusBadge tone="success">{fazendaSelecionada.nome}</StatusBadge>}
              >
                  {!fazendaSelecionada && (
                    <div className="alert alert-warning mb-0">
                      Cadastre uma fazenda para mostrar a previsão do tempo aqui.
                    </div>
                  )}

                  {fazendaSelecionada && weatherLoading && <div className="text-muted">Carregando previsão...</div>}

                  {fazendaSelecionada && !weatherLoading && weatherError && (
                    <div className="alert alert-warning mb-0">{weatherError}</div>
                  )}

                  {fazendaSelecionada && weather && (
                    <>
                      <div className="row g-3 align-items-center mb-3">
                        <div className="col-12 col-md-4">
                          <div className="display-6 fw-bold">{Math.round(weather.current?.temperature_2m ?? 0)}°C</div>
                          <div className="text-muted">{weatherLabels[weather.current?.weather_code] || 'Condicao atual'}</div>
                        </div>
                        <div className="col-6 col-md-2">
                          <span className="text-muted small d-block">Umidade</span>
                          <strong>{weather.current?.relative_humidity_2m ?? 0}%</strong>
                        </div>
                        <div className="col-6 col-md-2">
                          <span className="text-muted small d-block">Chuva agora</span>
                          <strong>{weather.current?.precipitation ?? 0} mm</strong>
                        </div>
                        <div className="col-6 col-md-2">
                          <span className="text-muted small d-block">Vento</span>
                          <strong>{weather.current?.wind_speed_10m ?? 0} km/h</strong>
                        </div>
                        <div className="col-6 col-md-2">
                          <span className="text-muted small d-block">Localização</span>
                          <strong>{weatherLocation?.cidade ? `${weatherLocation.cidade}${weatherLocation.estado ? ` - ${weatherLocation.estado}` : ''}` : 'Ponto da fazenda'}</strong>
                        </div>
                      </div>

                      <div className="row g-2">
                        {dailyWeather.map((dia) => (
                          <div className="col" key={dia.dia}>
                          <div className="cm-surface p-2 h-100">
                              <div className="fw-semibold text-capitalize">{dia.dia}</div>
                              <div className="small text-muted">{weatherLabels[dia.codigo] || 'Tempo'}</div>
                              <div className="mt-2">{Math.round(dia.min)}° / {Math.round(dia.max)}°</div>
                              <div className="small text-success">{dia.chuva}% chuva</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
              </ChartCard>
            </div>

            <div className="col-12 col-xl-5">
              <ChartCard
                title="Safras recentes"
                subtitle="Ciclos vinculados a fazenda selecionada"
                actions={fazendaSelecionada && <StatusBadge tone="success">{fazendaSelecionada.nome}</StatusBadge>}
              >
                  <div className="d-grid gap-2">
                    {safrasFiltradas.slice(0, 6).map((safra) => (
                      <div className="cm-surface p-3" key={safra.id}>
                        <div className="d-flex justify-content-between gap-3">
                          <strong>{safra.cultura}</strong>
                          <StatusBadge tone="success">{safra.statusDescricao || safra.status}</StatusBadge>
                        </div>
                        <small className="text-muted">
                          {safra.propriedadeNome || 'Fazenda não informada'} | {number(safra.areaHa, ' ha')}
                        </small>
                      </div>
                    ))}
                    {!safrasFiltradas.length && (
                      <div className="cm-surface p-3 cm-muted">Nenhuma safra cadastrada para esta fazenda.</div>
                    )}
                  </div>
              </ChartCard>
            </div>
          </div>
        </div>
    </>
  );
}

export default Dashboard;
